import {
    Cart,
    CartEntry,
    CustomerContractOption,
    EntryWithUpsell,
    ProductDeviceInCart,
    ProductVariant,
    VariantMatrix,
} from '@telialt/models/src/EshopModel';
import {
    selectFirstPurchasable,
    selectGiftEntry,
    selectHasRentPlan,
    selectIsFullPricePayment,
    selectLeasingPlan,
    selectMobilityRatePlanType,
    selectProductInCart,
    selectProductTotalPriceByPaymentMethod,
} from '@telialt/utils/src/eshopSelectors';
import { isEntryWithNetflix, isEntryWithRatePlan } from '@telialt/utils/src/eshopTypeGuards';
import { depthFirstSearch } from '@telialt/utils/src/eshopUtils';

import { DEFAULT_QUANTITY, normalizeNumber } from '../../core/normalizers';
import type { TrackingCommerce, TrackingItem } from '../../types';
import { GTMItemServiceType, GTMItemType, GTMOrderType } from '../../types';
import type { ProductUpsellEventInput } from './types';

const trimLeadingZeros = (text: string) => text?.replace(/^0+/, '');

const getProductServiceType = (cart: Cart) =>
    cart?.customerContractOption === CustomerContractOption.Existing
        ? GTMItemServiceType.Modification
        : GTMItemServiceType.NewSale;

export const getProductPageSummaryPrice = (cart: Cart) => {
    const product = selectProductInCart(cart);
    const productPriceInRentPayment = (selectHasRentPlan(cart) && product?.price?.value) || 0;
    const productPriceInLeasingPayment =
        (!selectIsFullPricePayment(cart) && (selectProductTotalPriceByPaymentMethod(cart) ?? product?.price)?.value) ||
        0;

    const isReverseVatApplied = cart?.reverseVatApplied;
    const isB2BCheckoutPage = cart?.registeredVatPayer != null;
    const totalPrice = (isB2BCheckoutPage && isReverseVatApplied === false ? cart?.totalPriceWithTax : cart?.totalPrice)
        ?.value;

    const insurancePriceFor1Month =
        (product?.insurancePossible && cart?.withInsurance && cart?.insuranceMonthlyPrice?.value) || 0;

    return (
        totalPrice +
        productPriceInRentPayment +
        productPriceInLeasingPayment -
        (cart?.selectedInitialPayment || 0) +
        insurancePriceFor1Month
    );
};

export const getProductPageLeasingParams = (cart: Cart) => {
    const productEntry = selectFirstPurchasable(cart);
    const leasingPlan = selectLeasingPlan(cart);
    const periodFromLeasingPlanCode = parseInt(leasingPlan?.code?.split('_')?.[1] || '', 10) || 0;
    let paymentPeriod: number | undefined;

    if (productEntry?.rentingPlanEntry) {
        paymentPeriod = productEntry.rentingPlanEntry.monthValue;
    } else if (productEntry && !selectIsFullPricePayment(cart)) {
        paymentPeriod = leasingPlan?.durationInMonths || periodFromLeasingPlanCode;
    }

    if (paymentPeriod) {
        return {
            leasing_monthly_value: productEntry?.monthlyPrice?.value || 0,
            instant_value: cart?.selectedInitialPayment ?? 0,
            leasing_period: paymentPeriod,
        };
    }

    return {
        leasing_monthly_value: leasingPlan?.monthlyPrice?.value || 0,
        instant_value: cart?.selectedInitialPayment ?? 0,
        leasing_period: leasingPlan?.durationInMonths || 0,
    };
};

const getNetflixPrice = (entry: CartEntry) => entry?.monthlyPrice?.value;

const isFreeNetflixEntry = (entry: CartEntry) => {
    const netflixPrice = getNetflixPrice(entry);

    return isEntryWithNetflix(entry) && netflixPrice !== undefined && netflixPrice <= 0;
};

const normalizeCartEntryToTrackingItem = (entry: CartEntry, serviceType: GTMItemServiceType): TrackingItem => {
    const isNetflix = isEntryWithNetflix(entry);
    const isNetflixGift = isFreeNetflixEntry(entry);
    const itemName = isNetflix ? 'Netflix' : entry.product?.name;
    const itemBrand = isNetflix ? 'Netflix' : entry.product?.brand;
    let itemCategory = entry.product?.category?.name;

    if (isNetflix) {
        itemCategory = 'OTT';
    } else if (isEntryWithRatePlan(entry)) {
        itemCategory = entry.product?.mobilityRatePlanType;
    }

    const itemListName = isNetflix ? 'OTT' : itemCategory;
    const netflixVariant = entry.product?.code === 'NETFLIX_PREMIUM_PRODUCT' ? 'Netflix Premium' : 'Netflix Standard';
    const productVariant = [entry.product?.productColor?.name, entry.product?.storageSize].filter(Boolean).join(' ');
    const itemVariant = isNetflix ? netflixVariant : productVariant;
    const price = isNetflix ? getNetflixPrice(entry) : entry.product?.price?.value;
    const currency = entry.product?.price?.currencyIso;
    const item: TrackingItem = {
        id: trimLeadingZeros(entry.product?.code),
        currency,
        index: entry.entryNumber,
        name: itemName,
        brand: itemBrand,
        category: itemCategory,
        listName: itemListName,
        price,
        quantity: entry?.quantity ?? DEFAULT_QUANTITY,
        serviceType,
    };

    if (isNetflixGift) {
        item.type = GTMItemType.Gift;
    }

    if (itemVariant) {
        item.variant = itemVariant;
    }

    return item;
};

export const mapProductPageCartToViewItemTrackingCommerce = (cart: Cart): TrackingCommerce | undefined => {
    const productEntry = selectFirstPurchasable(cart);

    if (!productEntry) {
        return undefined;
    }

    const viewItem = normalizeCartEntryToTrackingItem(productEntry, getProductServiceType(cart));

    return {
        currency: viewItem.currency,
        value: viewItem.price,
        itemListName: viewItem.listName,
        items: [viewItem],
    };
};

export const mapProductPageCartToAddToCartTrackingCommerce = (cart: Cart): TrackingCommerce => {
    const serviceType = getProductServiceType(cart);
    const items = cart?.entries?.map((entry) => normalizeCartEntryToTrackingItem(entry, serviceType)) || [];
    const mainProduct = selectProductInCart(cart);

    if (mainProduct?.insurancePossible && cart?.withInsurance) {
        const price = cart?.insuranceMonthlyPrice;

        items.push({
            index: cart?.entries?.length,
            id: 'Insurance',
            name: 'Insurance',
            variant: 'Insurance',
            brand: 'Insurance',
            category: 'Insurance',
            listName: mainProduct?.category?.name,
            currency: price.currencyIso,
            price: price.value,
            quantity: DEFAULT_QUANTITY,
        });
    }

    const cartItems = cart?.entries || items.length ? items : undefined;

    return {
        orderType: selectMobilityRatePlanType(cart) ? GTMOrderType.Services : GTMOrderType.Equipment,
        currency: cart.totalPriceWithTax.currencyIso,
        value: getProductPageSummaryPrice(cart),
        giftIncluded: Boolean(selectGiftEntry(cart) || cart?.entries?.some(isFreeNetflixEntry)),
        itemListName: cartItems && cartItems.length > 1 ? 'Mixed' : cartItems?.[0]?.listName,
        items: cartItems,
    };
};

const normalizeProductDeviceToTrackingItem = (
    product: ProductDeviceInCart | ProductVariant,
    index: number,
): TrackingItem => {
    const productVariant = product as ProductVariant;
    const itemCategory = product?.category?.name || productVariant?.categoryName;

    const byProductCode = (el: VariantMatrix) => el.isLeaf && el.variantOption.code === product.code;
    const productFromMatrix = depthFirstSearch(productVariant?.variantMatrix, byProductCode);
    const variantFromMatrix = productFromMatrix?.variantValueCategory?.name;
    const variantFromProduct = [product?.productColor?.name, product?.storageSize].filter(Boolean).join(' ');

    return {
        id: trimLeadingZeros(product?.code),
        currency: product?.price?.currencyIso,
        index,
        brand: product?.brand,
        name: product?.name,
        category: itemCategory,
        listName: itemCategory,
        variant: variantFromProduct || variantFromMatrix,
        price: normalizeNumber(product?.price?.value),
        quantity: DEFAULT_QUANTITY,
        funnelCategory: itemCategory,
        funnelVariant: 'Priedai',
    };
};

const mapProductUpsellToTrackingCommerce = (
    data: { product: ProductDeviceInCart | ProductVariant; segment?: string },
    index: number,
): TrackingCommerce => {
    const item = normalizeProductDeviceToTrackingItem(data.product, index);

    return {
        segment: data.segment,
        currency: item.currency,
        value: item.price ?? 0,
        itemListName: item.listName,
        items: [item],
    };
};

export const mapProductUpsellToAddToCartTrackingCommerce = (
    data: ProductUpsellEventInput['data'],
): TrackingCommerce => {
    return mapProductUpsellToTrackingCommerce(data, data.cart.entries.length);
};

export const mapProductUpsellToRemoveFromCartTrackingCommerce = (
    cart: Cart,
    entry: EntryWithUpsell,
): TrackingCommerce => {
    return {
        ...mapProductUpsellToTrackingCommerce({ product: entry.product }, entry.entryNumber),
        value: getProductPageSummaryPrice(cart),
    };
};

export const mapProductUpsellToItemTrackingCommerce = (data: ProductUpsellEventInput['data']): TrackingCommerce =>
    mapProductUpsellToTrackingCommerce(data, 0);

export const mapProductInsuranceToTrackingCommerce = (cart: Cart): TrackingCommerce => {
    const mainProduct = selectProductInCart(cart);
    const price = cart?.insuranceMonthlyPrice;

    return {
        currency: price?.currencyIso,
        value: 0,
        items: [
            {
                index: cart?.entries?.length,
                id: 'Insurance',
                name: 'Insurance',
                variant: 'Insurance',
                brand: 'Insurance',
                category: 'Insurance',
                listName: mainProduct?.category?.name,
                currency: price?.currencyIso,
                price: price?.value,
                quantity: DEFAULT_QUANTITY,
            },
        ],
    };
};
